import React, { useState, useEffect } from "react";
import {
  FaTimes,
  FaStar,
  FaFire,
  FaTag,
  FaHeart,
  FaHome,
  FaShoppingCart,
  FaTruck,
  FaTh,
  FaSpa,
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import "../styles/mobilesidebar.css";
import { useImage } from "../contexts/ImageContext";

function MobileSidebar({ isVisible, onClose }) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { categories } = useSelector((state) => state.categories);
  const { items: cartItems = [] } = useSelector((state) => state.cart);
  const location = useLocation();
  const cartItemCount = cartItems.length;
  const { getImageUrl } = useImage();

  // Recursive function to count total nested children
  const countNestedChildren = (category) => {
    if (
      !category.children ||
      !Array.isArray(category.children) ||
      category.children.length === 0
    ) {
      return 0;
    }

    let count = category.children.length;
    category.children.forEach((child) => {
      count += countNestedChildren(child);
    });

    return count;
  };

  // Sort categories by number of nested children (descending)
  const sortCategoriesByChildren = (categoriesList) => {
    if (!categoriesList || !Array.isArray(categoriesList)) {
      return [];
    }

    return [...categoriesList].sort((a, b) => {
      const aCount = countNestedChildren(a);
      const bCount = countNestedChildren(b);
      return bCount - aCount; // Descending order (more children first)
    });
  };

  useEffect(() => {
    if (isVisible && categories?.length > 0 && !selectedCategory) {
      const sortedCategories = sortCategoriesByChildren(categories);
      setSelectedCategory(sortedCategories[0]);
    }
  }, [isVisible, categories]);

  const categoryIcons = {
    Skincare: "🧴",
    Makeup: "💄",
    "Bath & Body": "🛁",
    Hair: "💇",
    "Face Masks": "😷",
    Suncare: "☀️",
  };

  // Recursive function to get all nested subcategories
  const getSubcategories = (category) => {
    // Handle both null/undefined and empty array cases
    if (
      !category?.children ||
      !Array.isArray(category.children) ||
      category.children.length === 0
    ) {
      return [];
    }

    return category.children.map((child) => {
      const hasChildren =
        child.children &&
        Array.isArray(child.children) &&
        child.children.length > 0;

      return {
        name: child.name,
        id: child.category_id,
        items: hasChildren
          ? sortCategoriesByChildren(child.children).map((grandchild) => {
              const hasGreatGrandchildren =
                grandchild.children &&
                Array.isArray(grandchild.children) &&
                grandchild.children.length > 0;

              return {
                name: grandchild.name,
                id: grandchild.category_id,
                items: hasGreatGrandchildren
                  ? sortCategoriesByChildren(grandchild.children).map(
                      (greatGrandchild) => ({
                        name: greatGrandchild.name,
                        id: greatGrandchild.category_id,
                      }),
                    )
                  : [],
              };
            })
          : [],
      };
    });
  };

  return (
    <div className={isVisible ? "mobile-sidebar visible" : "mobile-sidebar"}>
      <div className="close-button">
        <button onClick={onClose}>
          <FaTimes />
        </button>
      </div>

      <div className="categories-nav">
        {sortCategoriesByChildren(categories || []).map((category) => (
          <div
            key={category.category_id}
            className={`category-item ${
              selectedCategory?.category_id === category.category_id
                ? "active"
                : ""
            }`}
            onClick={() => {
              setSelectedCategory(category);
            }}
          >
            <span>{categoryIcons[category.name] || "📦"}</span>
            <span>{category.name}</span>
          </div>
        ))}
      </div>

      {selectedCategory && (
        <div className="subcategories-panel">
          <div className="subcategories-header">
            <h2 className="category-title">{selectedCategory.name} Products</h2>
            {/* Show parent category as clickable */}
            <Link
              to={`/search?category=${selectedCategory.category_id}`}
              className="parent-category-link"
              onClick={onClose}
            >
              View All {selectedCategory.name}
            </Link>
          </div>

          <div className="subcategories-container">
            {getSubcategories(selectedCategory).length > 0 ? (
              getSubcategories(selectedCategory).map((section) => (
                <div key={section.id} className="subcategory-section">
                  {/* Child category header - clickable */}
                  <Link
                    to={`/search?category=${section.id}`}
                    className="subcategory-section-title"
                    onClick={onClose}
                  >
                    <h3>{section.name}</h3>
                  </Link>

                  {/* Render nested subcategories */}
                  {section.items && section.items.length > 0 && (
                    <div className="subcategories-grid">
                      {section.items.map((item) => (
                        <div key={item.id} className="subcategory-group">
                          {/* Grandchild category - clickable */}
                          <Link
                            to={`/category/${item.id}`}
                            className="subcategory-item"
                            onClick={onClose}
                          >
                            <span>{item.name}</span>
                          </Link>

                          {/* Render great-grandchildren if they exist */}
                          {item.items && item.items.length > 0 && (
                            <div className="subcategory-nested-grid">
                              {item.items.map((nestedItem) => (
                                <Link
                                  key={nestedItem.id}
                                  to={`/search?category=${nestedItem.id}`}
                                  className="subcategory-nested-item"
                                  onClick={onClose}
                                >
                                  <span>{nestedItem.name}</span>
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p style={{ padding: "16px", color: "#666", fontSize: "14px" }}>
                No subcategories available
              </p>
            )}
          </div>
        </div>
      )}

      <div className={`bottom-nav-links ${isVisible ? "hidden" : ""}`}>
        {/* <Link
          to="/"
          className={`bottom-nav-item ${
            location.pathname === "/" ? "active" : ""
          }`}
          onClick={onClose}
        >
          <span>
            <FaHome className="bottom-nav-icon" />
          </span>
          <span>Home</span>
        </Link> */}
        <Link
          to="/all-products"
          className={`bottom-nav-item ${
            location.pathname === "/all-products" ? "active" : ""
          }`}
          onClick={onClose}
        >
          <span>
            <FaTh className="bottom-nav-icon" />
          </span>
          <span>All</span>
        </Link>
        <Link
          to="/best"
          className={`bottom-nav-item ${
            location.pathname === "/best" ? "active" : ""
          }`}
          onClick={onClose}
        >
          <span>
            <FaStar className="bottom-nav-icon" />
          </span>
          <span>Best</span>
        </Link>
        <Link
          to="/cart"
          className={`bottom-nav-item ${
            location.pathname === "/cart" ? "active" : ""
          }`}
          onClick={onClose}
        >
          <span className="cart-icon-container">
            <FaShoppingCart className="bottom-nav-icon" />
            {cartItemCount > 0 && (
              <span className="header-cart-badge">{cartItemCount}</span>
            )}
          </span>
          <span>Cart</span>
        </Link>
        <Link
          to="/new"
          className={`bottom-nav-item ${
            location.pathname === "/new" ? "active" : ""
          }`}
          onClick={onClose}
        >
          <span>
            <FaFire className="bottom-nav-icon" />
          </span>
          <span>New</span>
        </Link>
        <Link
          to="/routines"
          className={`bottom-nav-item ${
            location.pathname.startsWith("/routines") ? "active" : ""
          }`}
          onClick={onClose}
        >
          <span>
            <FaSpa className="bottom-nav-icon" />
          </span>
          <span>Routines</span>
        </Link>
      </div>
    </div>
  );
}

export default MobileSidebar;
